// Общее для экранов: кнопки в списках (открыть сессию, записать в окно, перейти), напоминание клиенту,
// выбор клиента, выбор встречи, цена по правилу для записи.
import { MEET, priceFor, type ClientOut, type MeetKind, type Price } from '@praktika/core';
import { call } from '../lib/api.ts';
import { $, $$, copyText, esc } from '../lib/dom.ts';
import { groupLabel, groupOf, isProjectClient, projects, typeById, typeName } from '../lib/data.ts';
import { go, setTab } from '../lib/nav.ts';
import { bindSearch, chooseSheet, closeSheet, openSheet, promptSheet } from '../lib/sheet.ts';
import { S } from '../lib/state.ts';
import { nowInfo } from '../lib/zones.ts';
import { sessionSheet } from './session.ts';

export function bindCommon(root: ParentNode): void {
  $$('[data-tab]', root).forEach((b) => { b.onclick = () => setTab(b.getAttribute('data-tab')!); });
  $$('[data-go]', root).forEach((b) => { b.onclick = () => go(b.getAttribute('data-go')!, {}); });
  $$('[data-book]', root).forEach((b) => {
    b.onclick = () => go('book', { date: b.getAttribute('data-book-date') || nowInfo().date,
      time: Number(b.getAttribute('data-book')), view: b.getAttribute('data-book-tz') || '' });
  });
  $$('[data-sess-open]', root).forEach((b) => { b.onclick = () => sessionSheet(b.getAttribute('data-sess-open')!); });
  $$('.next[data-sess]', root).forEach((b) => {
    b.onclick = (e) => {
      if ((e.target as HTMLElement).closest('a,button')) return;
      sessionSheet(b.getAttribute('data-sess')!);
    };
  });
  $$('[data-remind]', root).forEach((b) => { b.onclick = () => remind(b.getAttribute('data-remind')!); });
}

interface ReminderOut { text: string; link: string; messenger: string; contact: string }

export function remind(sessionId: string): void {
  void call<ReminderOut>('reminder', sessionId).then((r) => {
    const sh = openSheet('<div class="ttl">Написать клиенту</div><p class="sub">' + esc(r.messenger) +
      (r.contact ? ' · ' + esc(r.contact) : ' · контакт не указан') +
      '</p><div class="pre" style="margin:0 0 12px">' + esc(r.text) + '</div><div class="btnrow" style="padding:0">' +
      (r.contact ? '<a class="btn pri" href="' + esc(r.link) + '" target="_blank" rel="noopener">Открыть ' +
      esc(r.messenger) + '</a>' : '') + '<button class="btn sec" data-copy>Скопировать текст</button></div>');
    $('[data-copy]', sh).onclick = () => copyText(r.text);
  }, () => {});
}

// Выбор клиента (и, если нужно, коммерческого проекта — например, кто заплатил).
export function pickClient(cb: (id: string) => void, includeProjects = false): void {
  const list = S.clients.filter((c) => !c.archived).sort((a, b) => a.name.localeCompare(b.name));
  const sh = openSheet('<div class="ttl">Клиент</div><div class="search" style="margin:0 0 8px">' +
    '<input placeholder="Поиск" data-q></div><div class="pick">' +
    list.map((c) => '<button class="opt" data-id="' + esc(c.id) + '"><span>' + esc(c.name) +
      '</span><span class="hint">' + esc(groupLabel(c) + ' · ' + typeName(c.effTypeId || c.typeId)) + '</span></button>')
      .join('') +
    (includeProjects ? projects().map((p) => '<button class="opt" data-id="' + esc(p.id) + '"><span>' + esc(p.name) +
      ' <span class="tag p">проект</span></span></button>').join('') : '') + '</div>');
  const q = $<HTMLInputElement>('[data-q]', sh);
  bindSearch(q, sh);
  $$('.pick .opt', sh).forEach((o) => { o.onclick = () => { closeSheet(); cb(o.getAttribute('data-id')!); }; });
  setTimeout(() => q.focus(), 50);
}

export interface MeetTarget { meet: MeetKind | ''; link: string; address: string }

// Встреча для записи или сессии: вид, а для ссылки или очной встречи — сама ссылка или адрес.
export function meetSheet(target: MeetTarget, c: Pick<ClientOut, 'meet' | 'link' | 'address' | 'groupId'> | null,
  done: () => void): void {
  const kinds: MeetKind[] = [MEET.MEET, MEET.ZOOM, MEET.OWN_LINK, MEET.NONE, MEET.OFFLINE];
  if (c && isProjectClient(c)) kinds.unshift(MEET.BY_GROUP);
  const labels: Record<MeetKind, string> = { meet: 'Google Meet, новая ссылка',
    zoom: 'Zoom, новая ссылка (Zoom не подключён: ссылка руками)', own_link: 'Постоянная ссылка клиента',
    none: 'Без встречи', offline: 'Очно', by_group: 'Ссылку присылает проект' };
  const cur = target.meet || (c ? c.meet : S.settings.meet_kind_default);
  chooseSheet('Встреча', kinds.map((k) => ({ value: k, label: labels[k] })), cur, (k) => {
    target.meet = k;
    if (k === MEET.OWN_LINK || k === MEET.BY_GROUP || k === MEET.ZOOM) {
      promptSheet('Ссылка', target.link || (c ? c.link : '') || '', 'url', (v) => { target.link = v; done(); });
    } else if (k === MEET.OFFLINE) {
      const addrs = S.addresses.slice();
      if (c && c.address && !addrs.includes(c.address)) addrs.unshift(c.address);
      addrs.push('__new');
      chooseSheet('Адрес', addrs.map((a) => ({ value: a, label: a === '__new' ? 'Другой адрес…' : a })),
        target.address || (c ? c.address : ''), (a) => {
          if (a === '__new') promptSheet('Адрес', '', 'text', (v) => { target.address = v; done(); });
          else { target.address = a; done(); }
        });
    } else { target.link = ''; done(); }
  });
}

// Цена записи по правилу: своя клиента → группы → типа сессии.
export function bookPrice(c: ClientOut, typeId?: string | null): Price {
  return priceFor(c, groupOf(c), typeById(typeId || c.effTypeId || c.typeId), S.settings.main_currency);
}
